import React, { useCallback, useEffect, useMemo } from "react";

import { useNavigate, useParams } from "react-router";
import { TFunction } from "i18next";

import { CrossSellList } from "@companion-core/shared/app/Components/CrossSell/CrossSellList";
import { useCrossSell } from "@companion-core/shared/app/Components/CrossSell/hooks/useCrossSell";
import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import { CrossSellProps } from "@companion-core/shared/app/Components/CrossSell/crossSell.model";
import { APICrossSell } from "@companion-core/shared/app/Interfaces/API";
import { useMediaQuery } from "@companion-core/web/src/Utilities/mediaQuery";
import { SubCrossSell } from "@companion-core/shared/app/Components/CrossSell/SubCrossSell";
import { Button } from "@companion-core/shared/app/Components/Button";
import {
  trackPageCrossSell,
  trackPageCrossSellGroupe,
  trackCrossSellPublisherImpression,
} from "@companion-core/shared/app/tracking/crossSell";

import "@companion-core/web/src/Assets/Styles/scss/crossSell.scss";
import { useConfigStore } from "@companion-core/shared/app/Store/configStore";

const Notification = ({ t }: { t: TFunction }) => {
  return (
    <div className="cross-sell__content">
      <div className="cross-sell__content-icon" data-testid="cross-sell__content-icon">
        <FontIcon name="JP-Hourglass" size={30} />
      </div>
      <h2 className="body-text-5" data-testid="cross-sell__content-title">
        {t("request.my-requests.recommendation.popin.acceptation.title")}
      </h2>
      <p className="body-text-8" data-testid="cross-sell__content-description">
        {t("request.my-requests.recommendation.popin.acceptation.text")}
      </p>
    </div>
  );
};

export const CrossSell: React.FC<CrossSellProps> = () => {
  const { id, subId } = useParams();
  const isMobileSize = useMediaQuery("(max-width: 768px)");
  const navigate = useNavigate();
  const { config } = useConfigStore();
  const isCrossSellEnabled = useMemo(() => config?.modules?.crossSell?.enabled, [config]);
  const { t, data, isLoading, isError } = useCrossSell({
    id,
    enabled: isCrossSellEnabled,
  });
  const subCrossSell = useMemo(() => {
    if (data && subId) return (data as APICrossSell[])?.find((value) => value.id === subId);
  }, [data, subId]);
  const navigateToMyRequest = useCallback(() => {
    navigate(`/my-requests/${id}/details/detail`);
  }, [id]);

  // Tracking page display
  useEffect(() => {
    if (!id) return;
    if (subCrossSell) {
      trackPageCrossSellGroupe({ requestId: id });
    } else if (!subId) {
      trackPageCrossSell({ requestId: id });
    }
  }, [id, subId, subCrossSell]);

  // Publishers tracking when the member sees the cards (cross-sell page)
  useEffect(() => {
    if (!id) return;
    if (subCrossSell?.cross_sell_concierge?.sub_cross_sells) {
      const cards = subCrossSell.cross_sell_concierge.sub_cross_sells;
      cards.forEach((element, index) => {
        trackCrossSellPublisherImpression({
          pageSlug: "cross-sell-groupe",
          family: element.family,
          subFamily: element.subfamily,
          positionItem: index,
          nbItems: cards.length,
          url: element.bell_deep_link || "",
          contentId: element.id,
        });
      });
    } else if (!subId && data && (data as APICrossSell[]).length > 0) {
      const cards = data as APICrossSell[];
      cards.forEach((element, index) => {
        trackCrossSellPublisherImpression({
          pageSlug: "cross-sell",
          family: element.family,
          subFamily: element.subfamily,
          positionItem: index,
          nbItems: cards.length,
          url: element.bell_deep_link || "",
          contentId: element.id,
        });
      });
    }
  }, [id, subId, subCrossSell, data]);

  const NoCrossSell = () => {
    return (
      <div className="cross-sell--error-content" data-testid="cross-sell-error-content">
        <Button
          label={t("cross-sell.error.button")}
          testID="cross-sell-error-button"
          onClick={() => navigateToMyRequest()}
          size="full"
          type="primary"
          classNames={["cross-sell--error-button"]}
        />
      </div>
    );
  };

  return (
    <div
      className={`cross-sell${subCrossSell ? " cross-sell--sub-cross-sell" : ""}`}
      data-testid="cross-sell">
      {isCrossSellEnabled ? (
        <>
          {subCrossSell ? (
            <>
              {isMobileSize ? (
                <SubCrossSell data={subCrossSell} requestId={id} />
              ) : (
                <>
                  <Notification t={t} />
                  <CrossSellList
                    title={subCrossSell.title}
                    data={subCrossSell.cross_sell_concierge.sub_cross_sells}
                    requestId={id}
                    canGoBack
                    onCancel={() =>
                      navigate(`/my-requests/${id}/details/detail`, { replace: true })
                    }
                  />
                </>
              )}
            </>
          ) : (
            !subId && (
              <>
                <Notification t={t} />
                {!isLoading &&
                  (!isError && data?.length ? (
                    <CrossSellList
                      data={data}
                      requestId={id}
                      onCancel={() =>
                        navigate(`/my-requests/${id}/details/detail`, { replace: true })
                      }
                    />
                  ) : (
                    <NoCrossSell />
                  ))}
              </>
            )
          )}
        </>
      ) : (
        <>
          <Notification t={t} />
          <NoCrossSell />
        </>
      )}
    </div>
  );
};
